/**
 * Applier for the order-aware IScreenDesign collab ops.
 *
 * `applyCollabOps(sd, ops)` mutates `sd` in place, applying ops sequentially.
 * An op whose target no longer exists (node gone, parent gone, path unresolvable)
 * is SKIPPED into `skippedOps` — never thrown: collab op streams race against
 * concurrent edits and echoes, and a skipped op is a normal outcome.
 *
 * Insert position resolution (nodeAdd / nodeMove):
 *  1. `resolvedIndex` wins when present (server-authoritative; clamped to bounds);
 *  2. else `afterSiblingId` — null inserts first, a missing sibling appends;
 *  3. else append.
 *
 * nodeMove detaches FIRST and only then resolves the insertion index against the
 * new sibling array (correct for same-parent reorders), and skips any move that
 * would create a cycle (target parent inside — or equal to — the moved subtree).
 * nodeAdd skips when the payload id already exists anywhere in the tree (the server
 * remaps ids; a duplicate reaching apply is an echo).
 *
 * `parentId` maintenance: moved/added nodes get their `parentId` field set to the
 * target parent id (numeric when it parses as an integer — matching how the tree
 * stores ids). Root-level: adds keep whatever the payload had; moves clear it.
 */

import { IDisplayItem } from "../../models/display-item";
import { IScreenDesign } from "../../models/screen-design";
import { cloneJsonValue, CollabJsonValue, ICollabJsonObject, isJsonArray, isJsonObject } from "./sd-json";
import { ICollabApplyResult, ICollabOp } from "./sd-patch-types";
import {
  CollabPathSegment,
  denormalizeNodeId,
  normalizeNodeId,
  parsePointer,
  removeAtPath,
  setAtPath
} from "./sd-path";

interface INodeLocation {
  node: IDisplayItem;
  siblings: IDisplayItem[];
  index: number;
}

/** Runtime view of the parent link: the model declares a number, the wire may deliver a string. */
interface IRuntimeParentIdCarrier {
  parentId?: number | string;
}

/**
 * Node-root keys structural ops own exclusively; a propSet/propRemove touching them
 * could silently corrupt tree topology, so such paths are refused (defense in depth —
 * the differ never emits them). Nested occurrences (e.g. "/item/children") stay legal.
 */
const FORBIDDEN_NODE_ROOT_KEYS: ReadonlySet<string> = new Set<string>(["id", "children", "parentId"]);
const FORBIDDEN_SCREEN_ROOT_KEYS: ReadonlySet<string> = new Set<string>(["displayItems", "id"]);

function locateNode(displayItems: IDisplayItem[], idNorm: string): INodeLocation | null {
  for (let i = 0; i < displayItems.length; i++) {
    const node: IDisplayItem = displayItems[i];
    if (normalizeNodeId(node.id) === idNorm) {
      return { node, siblings: displayItems, index: i };
    }
    if (Array.isArray(node.children)) {
      const found: INodeLocation | null = locateNode(node.children, idNorm);
      if (found !== null) {
        return found;
      }
    }
  }
  return null;
}

function subtreeContainsId(root: IDisplayItem, idNorm: string): boolean {
  if (normalizeNodeId(root.id) === idNorm) {
    return true;
  }
  if (Array.isArray(root.children)) {
    for (const child of root.children) {
      if (subtreeContainsId(child, idNorm)) {
        return true;
      }
    }
  }
  return false;
}

function ensureChildrenArray(node: IDisplayItem): IDisplayItem[] {
  if (!Array.isArray(node.children)) {
    node.children = [];
  }
  return node.children;
}

function resolveInsertionIndex(op: ICollabOp, siblings: readonly IDisplayItem[]): number {
  if (typeof op.resolvedIndex === "number" && Number.isFinite(op.resolvedIndex)) {
    const truncated: number = Math.trunc(op.resolvedIndex);
    return Math.min(Math.max(truncated, 0), siblings.length);
  }
  if (op.afterSiblingId === null) {
    return 0;
  }
  if (typeof op.afterSiblingId === "string") {
    const anchorNorm: string = normalizeNodeId(op.afterSiblingId);
    for (let i = 0; i < siblings.length; i++) {
      if (normalizeNodeId(siblings[i].id) === anchorNorm) {
        return i + 1;
      }
    }
    return siblings.length;
  }
  return siblings.length;
}

function setRuntimeParentId(node: IDisplayItem, parentIdNorm: string): void {
  const carrier: IRuntimeParentIdCarrier = node as IRuntimeParentIdCarrier;
  if (parentIdNorm === "") {
    carrier.parentId = undefined;
  } else {
    carrier.parentId = denormalizeNodeId(parentIdNorm);
  }
}

function applyNodeAdd(sd: IScreenDesign, op: ICollabOp): boolean {
  const payload: unknown = op.nodePayload;
  if (!isJsonObject(payload)) {
    return false;
  }
  const payloadId: CollabJsonValue = payload.id;
  if (typeof payloadId !== "string" && typeof payloadId !== "number") {
    return false;
  }
  const idNorm: string = normalizeNodeId(payloadId);
  if (idNorm === "") {
    return false;
  }
  if (locateNode(sd.displayItems, idNorm) !== null) {
    // Echo of an already-applied add (the server remaps genuinely new ids).
    return false;
  }
  const parentIdNorm: string = normalizeNodeId(op.parentId);
  let targetSiblings: IDisplayItem[];
  if (parentIdNorm === "") {
    targetSiblings = sd.displayItems;
  } else {
    const parentLocation: INodeLocation | null = locateNode(sd.displayItems, parentIdNorm);
    if (parentLocation === null) {
      return false;
    }
    targetSiblings = ensureChildrenArray(parentLocation.node);
  }
  const clonedPayload: ICollabJsonObject = cloneJsonValue(payload) as ICollabJsonObject;
  if (!isJsonArray(clonedPayload.children)) {
    clonedPayload.children = [];
  }
  const newNode: IDisplayItem = clonedPayload as unknown as IDisplayItem;
  if (parentIdNorm !== "") {
    setRuntimeParentId(newNode, parentIdNorm);
  }
  const insertionIndex: number = resolveInsertionIndex(op, targetSiblings);
  targetSiblings.splice(insertionIndex, 0, newNode);
  return true;
}

function applyNodeRemove(sd: IScreenDesign, op: ICollabOp): boolean {
  const idNorm: string = normalizeNodeId(op.nodeId);
  if (idNorm === "") {
    return false;
  }
  const location: INodeLocation | null = locateNode(sd.displayItems, idNorm);
  if (location === null) {
    return false;
  }
  location.siblings.splice(location.index, 1);
  return true;
}

function applyNodeMove(sd: IScreenDesign, op: ICollabOp): boolean {
  const idNorm: string = normalizeNodeId(op.nodeId);
  if (idNorm === "") {
    return false;
  }
  const location: INodeLocation | null = locateNode(sd.displayItems, idNorm);
  if (location === null) {
    return false;
  }
  const parentIdNorm: string = normalizeNodeId(op.parentId);
  let targetSiblings: IDisplayItem[];
  if (parentIdNorm === "") {
    targetSiblings = sd.displayItems;
  } else {
    if (subtreeContainsId(location.node, parentIdNorm)) {
      // The target parent lives inside the moved subtree (or IS the node): cycle.
      return false;
    }
    const parentLocation: INodeLocation | null = locateNode(sd.displayItems, parentIdNorm);
    if (parentLocation === null) {
      return false;
    }
    targetSiblings = ensureChildrenArray(parentLocation.node);
  }
  // Detach first, then resolve the index against the NEW sibling array — this is
  // what makes same-parent reorders land on the right slot.
  location.siblings.splice(location.index, 1);
  const insertionIndex: number = resolveInsertionIndex(op, targetSiblings);
  targetSiblings.splice(insertionIndex, 0, location.node);
  setRuntimeParentId(location.node, parentIdNorm);
  return true;
}

function validatePath(path: string | undefined, forbiddenRootKeys: ReadonlySet<string>): boolean {
  if (typeof path !== "string") {
    return false;
  }
  const segments: CollabPathSegment[] | null = parsePointer(path);
  if (segments === null || segments.length === 0) {
    return false;
  }
  const first: CollabPathSegment = segments[0];
  if (first.kind === "plain" && forbiddenRootKeys.has(first.key)) {
    return false;
  }
  return true;
}

function applyPropSet(sd: IScreenDesign, op: ICollabOp): boolean {
  if (!validatePath(op.path, FORBIDDEN_NODE_ROOT_KEYS)) {
    return false;
  }
  const idNorm: string = normalizeNodeId(op.nodeId);
  if (idNorm === "") {
    return false;
  }
  const location: INodeLocation | null = locateNode(sd.displayItems, idNorm);
  if (location === null) {
    return false;
  }
  return setAtPath(location.node, op.path as string, cloneJsonValue(op.value));
}

function applyPropRemove(sd: IScreenDesign, op: ICollabOp): boolean {
  if (!validatePath(op.path, FORBIDDEN_NODE_ROOT_KEYS)) {
    return false;
  }
  const idNorm: string = normalizeNodeId(op.nodeId);
  if (idNorm === "") {
    return false;
  }
  const location: INodeLocation | null = locateNode(sd.displayItems, idNorm);
  if (location === null) {
    return false;
  }
  return removeAtPath(location.node, op.path as string);
}

function applyScreenPropSet(sd: IScreenDesign, op: ICollabOp): boolean {
  if (!validatePath(op.path, FORBIDDEN_SCREEN_ROOT_KEYS)) {
    return false;
  }
  return setAtPath(sd, op.path as string, cloneJsonValue(op.value));
}

function applyScreenPropRemove(sd: IScreenDesign, op: ICollabOp): boolean {
  if (!validatePath(op.path, FORBIDDEN_SCREEN_ROOT_KEYS)) {
    return false;
  }
  return removeAtPath(sd, op.path as string);
}

function applyOneOp(sd: IScreenDesign, op: ICollabOp): boolean {
  switch (op.kind) {
    case "nodeAdd":
      return applyNodeAdd(sd, op);
    case "nodeRemove":
      return applyNodeRemove(sd, op);
    case "nodeMove":
      return applyNodeMove(sd, op);
    case "propSet":
      return applyPropSet(sd, op);
    case "propRemove":
      return applyPropRemove(sd, op);
    case "screenPropSet":
      return applyScreenPropSet(sd, op);
    case "screenPropRemove":
      return applyScreenPropRemove(sd, op);
    default:
      return false;
  }
}

export function applyCollabOps(sd: IScreenDesign, ops: ICollabOp[]): ICollabApplyResult {
  if (!Array.isArray(sd.displayItems)) {
    sd.displayItems = [];
  }
  const skippedOps: ICollabOp[] = [];
  let appliedCount: number = 0;
  for (const op of ops) {
    const applied: boolean = applyOneOp(sd, op);
    if (applied) {
      appliedCount++;
    } else {
      skippedOps.push(op);
    }
  }
  return { appliedCount, skippedOps };
}
